// src/components/admin/qtl - referential/issuers/columns.tsx

import { formatNumber, parseDateWithHoursUTC } from "@/lib/utils";
import i18n from "@/i18n";
import { Link } from "@heroui/link";
import { ColumnDef } from "@tanstack/react-table";
import { issuerTypeOptions, sectorOptions } from "./filter";

// Helper functions to convert enum values to labels
const getEnumLabel = (
  value: any,
  options: { value: string; label: string }[],
): string => {
  if (value === null || value === undefined) return "";
  const option = options.find((opt) => opt.value === String(value));
  return option ? option.label : String(value);
};

// Type of the fields in the table
export type Issuer = {
  id: number;
  lei: string | null;
  name: string | null;
  country?: string | null;
  fund_registration_country?: string | null;
  fund_registration_country_name?: string | null;
  fund_registration_country_id?: number | null;
  sector?: string | null;
  industry?: string | null;
  issuer_type?: string | null;
  nb_constituents: number;
  market_cap_amount: number | null;
  market_cap_currency: string | null;
  source: string | null;
  created_at: string;
};

// Definition of the columns
export const issuers_columns: ColumnDef<Issuer>[] = [
  {
    accessorKey: "id",
    header: "Id",
  },
  {
    accessorKey: "lei",
    header: "Lei",
  },
  {
    accessorKey: "name",
    header: "Name",
  },
  {
    accessorKey: "country",
    header: "Country",
  },
  {
    id: "fund_registration_country",
    accessorFn: (row) =>
      row.fund_registration_country ??
      row.fund_registration_country_name ??
      row.fund_registration_country_id ??
      "",
    header: "Fund Registration Country",
  },
  {
    accessorKey: "sector",
    header: "Sector",
    cell: ({ row }) => getEnumLabel(row.getValue("sector"), sectorOptions),
  },
  {
    accessorKey: "industry",
    header: "Industry",
  },
  {
    accessorKey: "issuer_type",
    header: "Issuer Type",
    cell: ({ row }) =>
      getEnumLabel(row.getValue("issuer_type"), issuerTypeOptions),
  },
  {
    accessorKey: "nb_constituents",
    header: () => <div className="text-right">Nb Constituents</div>,
    cell: ({ row }) => (
      <div className="text-right">{row.getValue("nb_constituents")}</div>
    ),
  },
  {
    accessorKey: "market_cap_amount",
    header: () => <div className="text-right">Market Cap Amount</div>,
    cell: ({ row }) => (
      <div className="text-right">
        {formatNumber(row.getValue("market_cap_amount"))}
      </div>
    ),
  },
  {
    accessorKey: "market_cap_currency",
    header: "Market Cap Currency",
  },
  {
    accessorKey: "source",
    header: "Source",
  },
  {
    accessorKey: "created_at",
    header: "Created At",
    cell: ({ row }) => parseDateWithHoursUTC(row.getValue("created_at")),
  },
  {
    id: "actions",
    header: () => <div className="text-right">Actions</div>,
    cell: ({ row }) => (
      <div className="flex justify-end space-x-2">
        {/* Add more actions here if needed */}
        <Link
          href={`${window.location.pathname}/${row.getValue("id")}`}
          className="text-xs"
          underline="hover"
        >
          {i18n.t("actions.edit")}
        </Link>
      </div>
    ),
  },
];
